import type { ActionInputField, SealedTypeSchema, VariantSchema } from '@/app/commons/generated'

/**
 * Rendering a sealed type from the schema the server publishes for it, rather than from a list of
 * fields written out here.
 *
 * The server reflects every sealed type — entity attributes, benefit terms, redemption constraints
 * — into a [SealedTypeSchema]: a discriminator plus one variant per subtype, each carrying its
 * fields with a label, a description, and a typed input. A client that hard-codes "headline, bio,
 * years, certifications" is a second copy of that model which nobody updates: add a field
 * server-side and it is simply invisible; remove one and the client renders a hole. Worse for a
 * form, a save that omits an unknown field silently clears it.
 *
 * Reading the schema instead means a new field or a whole new variant appears with no change here.
 * The admin panel already works this way (`SchemaFieldInput`); this is the same idea for web.
 *
 * A field may itself be sealed — `contacts` is a list of contact methods, each declaring its own
 * fields — and that is rendered by the same code, recursively: pick the entry's variant, then edit
 * the fields that variant declares.
 */

/** A sealed value: the discriminator plus whatever its variant declares. */
export type SealedValue = Record<string, unknown>

/** The variant a value belongs to, or null when it names one the schema does not describe. */
export const variantOf = (schema: SealedTypeSchema, value: SealedValue | null): VariantSchema | null => {
  if (!value) return null
  const discriminator = value[schema.discriminator]
  return schema.variants.find((variant) => variant.type === discriminator) ?? null
}

/** The declared input type, which the generated union types as an opaque object. */
type InputType = {
  kind?: string
  multiple?: boolean
  maxLength?: number
  maxItems?: number
  min?: number
  max?: number
  options?: { value: string; label: string }[]
  schema?: SealedTypeSchema
}

const typeOf = (field: ActionInputField): InputType => (field.type as unknown as InputType) ?? {}

/** `type.kind` off a field. */
const kindOf = (field: ActionInputField): string => String(typeOf(field).kind ?? 'text')

const bound = (field: ActionInputField, name: 'min' | 'max' | 'maxLength' | 'maxItems'): number | undefined =>
  typeOf(field)[name]

const options = (field: ActionInputField): { value: string; label: string }[] => typeOf(field).options ?? []

/** The nested schema of a `sealed` field, or null for any other field. */
const nestedSchema = (field: ActionInputField): SealedTypeSchema | null =>
  kindOf(field) === 'sealed' ? (typeOf(field).schema ?? null) : null

/**
 * Whether a field is a list of strings.
 *
 * Read from the schema's `multiple` flag, not from the value: a profile never saved has no
 * `certifications` at all, and deciding from the value drew a plain text box there that posted a
 * string where the server binds a list. An array value is still honored, for a server that does not
 * send the flag.
 */
const isTextList = (field: ActionInputField, current: unknown): boolean =>
  (kindOf(field) === 'text' && typeOf(field).multiple === true) ||
  (Array.isArray(current) && current.every((entry) => typeof entry === 'string'))

const isSealedValue = (value: unknown): value is SealedValue =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * What a plain value reads as.
 *
 * Arrays of strings are joined rather than listed: a `List<String>` is published as a `text` input,
 * and a renderer that trusted the declared kind alone would print `[object Object]` for them.
 */
const displayValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  if (Array.isArray(value)) return value.filter((entry) => typeof entry === 'string' && entry).join(', ')
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

/** One sealed entry as a line of text: its variant's label, then the values of its fields. */
const describeEntry = (schema: SealedTypeSchema, entry: SealedValue): { label: string; text: string } | null => {
  const variant = variantOf(schema, entry)
  if (!variant) return null
  const text = variant.fields
    .map((field) => displayValue(entry[field.key]))
    .filter(Boolean)
    .join(' · ')
  return text ? { label: variant.label, text } : null
}

/**
 * Where an entry should go when clicked, if the server says it goes anywhere.
 *
 * The server computes `link` for the variants that have one (a `mailto:`, a WhatsApp chat), so this
 * knows nothing about which variant it is looking at. Only schemes a browser can safely follow are
 * made clickable.
 */
const linkOf = (entry: SealedValue): string | null => {
  const link = entry.link
  if (typeof link !== 'string') return null
  return /^(https?:|mailto:|tel:|sms:)/i.test(link) ? link : null
}

/** Read-only rendering of a list (or one) of sealed entries. */
const SealedEntriesView = ({ schema, value }: { schema: SealedTypeSchema; value: unknown }) => {
  const entries = (Array.isArray(value) ? value : [value]).filter(isSealedValue)
  const described = entries
    .map((entry) => ({ entry, shown: describeEntry(schema, entry) }))
    .filter((item): item is { entry: SealedValue; shown: { label: string; text: string } } => item.shown !== null)
  if (described.length === 0) return null
  return (
    <ul className="space-y-1">
      {described.map(({ entry, shown }, index) => {
        const link = linkOf(entry)
        return (
          <li key={index} className="text-sm text-gray-900">
            <span className="text-gray-500">{shown.label}: </span>
            {link ? (
              <a
                href={link}
                target={link.startsWith('http') ? '_blank' : undefined}
                rel="noopener noreferrer"
                className="text-primary-600 hover:underline"
              >
                {shown.text}
              </a>
            ) : (
              shown.text
            )}
          </li>
        )
      })}
    </ul>
  )
}

/** Whether a field has anything to show. */
const hasValue = (field: ActionInputField, value: unknown): boolean => {
  const schema = nestedSchema(field)
  if (schema) {
    const entries = (Array.isArray(value) ? value : [value]).filter(isSealedValue)
    return entries.some((entry) => describeEntry(schema, entry) !== null)
  }
  return displayValue(value) !== ''
}

/** Read-only rendering of every field the value's variant declares and has a value for. */
export const SealedFieldsView = ({
  schema,
  value,
  className = '',
  omit = [],
}: {
  schema: SealedTypeSchema
  value: SealedValue | null
  className?: string
  /**
   * Keys already shown elsewhere on the page.
   *
   * Presentation only — a promoted field is still saved, still edited and still read off the
   * schema; it is just not printed twice. This is the one place a caller is allowed to name a
   * field, and it is safe precisely because omitting one here cannot reach a save.
   */
  omit?: string[]
}) => {
  const variant = variantOf(schema, value)
  if (!variant || !value) return null

  const shown = variant.fields.filter((field) => !omit.includes(field.key) && hasValue(field, value[field.key]))

  if (shown.length === 0) return null

  return (
    <dl className={className}>
      {shown.map((field) => {
        const nested = nestedSchema(field)
        return (
          <div key={field.key} className="mb-3 last:mb-0">
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{field.label}</dt>
            <dd className="mt-0.5 whitespace-pre-line text-sm text-gray-900">
              {nested ? <SealedEntriesView schema={nested} value={value[field.key]} /> : displayValue(value[field.key])}
            </dd>
          </div>
        )
      })}
    </dl>
  )
}

/** A new, empty entry of the given variant: just its discriminator, the fields to be filled in. */
const emptyEntry = (schema: SealedTypeSchema, type: string): SealedValue => ({ [schema.discriminator]: type })

/**
 * One sealed entry: which variant it is, and that variant's fields.
 *
 * Changing the variant starts the entry again, since the fields of one method mean nothing to
 * another — a phone number is not an email address.
 */
const SealedEntryEditor = ({
  schema,
  value,
  onChange,
  disabled,
  idPrefix,
}: {
  schema: SealedTypeSchema
  value: SealedValue
  onChange: (next: SealedValue) => void
  disabled: boolean
  idPrefix: string
}) => {
  const current = variantOf(schema, value)
  return (
    <div className="space-y-3">
      <select
        id={`${idPrefix}-variant`}
        aria-label="Type"
        className="w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
        disabled={disabled}
        value={current?.type ?? ''}
        onChange={(e) => onChange(emptyEntry(schema, e.target.value))}
      >
        {!current && <option value="">—</option>}
        {schema.variants.map((variant) => (
          <option key={variant.type} value={variant.type}>
            {variant.label}
          </option>
        ))}
      </select>
      {current && (
        <SealedFieldsForm schema={schema} value={value} onChange={onChange} disabled={disabled} idPrefix={idPrefix} />
      )}
    </div>
  )
}

/**
 * A list of sealed entries: add one, pick what it is, fill it in, reorder, remove.
 *
 * Order is kept because it can mean something — a coach lists the way they would rather be reached
 * first — so entries move up rather than being sorted.
 */
const SealedListEditor = ({
  field,
  schema,
  value,
  onChange,
  disabled,
  idPrefix,
}: {
  field: ActionInputField
  schema: SealedTypeSchema
  value: unknown
  onChange: (next: SealedValue[]) => void
  disabled: boolean
  idPrefix: string
}) => {
  const entries = Array.isArray(value) ? value.filter(isSealedValue) : []
  const maxItems = bound(field, 'maxItems')
  const atLimit = maxItems !== undefined && entries.length >= maxItems
  const firstVariant = schema.variants[0]?.type

  const replace = (index: number, next: SealedValue) =>
    onChange(entries.map((entry, i) => (i === index ? next : entry)))
  const remove = (index: number) => onChange(entries.filter((_, i) => i !== index))
  const moveUp = (index: number) => {
    if (index === 0) return
    const next = [...entries]
    ;[next[index - 1], next[index]] = [next[index], next[index - 1]]
    onChange(next)
  }

  return (
    <div className="mt-1 space-y-3">
      {entries.map((entry, index) => (
        <div key={index} className="rounded border border-gray-200 p-3">
          <SealedEntryEditor
            schema={schema}
            value={entry}
            onChange={(next) => replace(index, next)}
            disabled={disabled}
            idPrefix={`${idPrefix}-${index}`}
          />
          <div className="mt-2 flex gap-4 text-xs">
            {index > 0 && (
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700"
                disabled={disabled}
                onClick={() => moveUp(index)}
              >
                Move up
              </button>
            )}
            <button
              type="button"
              className="text-red-600 hover:text-red-800"
              disabled={disabled}
              onClick={() => remove(index)}
            >
              Remove
            </button>
          </div>
        </div>
      ))}
      {firstVariant && (
        <button
          type="button"
          className={`text-xs font-medium ${
            atLimit ? 'cursor-not-allowed text-gray-300' : 'text-primary-600 hover:text-primary-500'
          }`}
          disabled={disabled || atLimit}
          title={atLimit ? `At most ${maxItems}` : undefined}
          onClick={() => onChange([...entries, emptyEntry(schema, firstVariant)])}
        >
          + Add
        </button>
      )}
    </div>
  )
}

/**
 * An editable form for the value's variant.
 *
 * `onChange` is handed the whole value, discriminator included, so a caller never rebuilds it from
 * parts — rebuilding is exactly how the fields this form does not know about get dropped.
 */
export const SealedFieldsForm = ({
  schema,
  value,
  onChange,
  disabled = false,
  idPrefix = 'sealed',
}: {
  schema: SealedTypeSchema
  value: SealedValue
  onChange: (next: SealedValue) => void
  disabled?: boolean
  /** Keeps element ids unique when the same form appears more than once, as in a list. */
  idPrefix?: string
}) => {
  const variant = variantOf(schema, value)
  if (!variant) return null

  const set = (key: string, next: unknown) => onChange({ ...value, [key]: next })

  return (
    <div className="space-y-4">
      {variant.fields.map((field) => {
        const kind = kindOf(field)
        const current = value[field.key]
        const id = `${idPrefix}-${variant.type}-${field.key}`
        const nested = nestedSchema(field)

        return (
          <div key={field.key}>
            <label className="block text-xs font-medium text-gray-700" htmlFor={id}>
              {field.label}
              {field.required && <span className="ml-0.5 text-red-600">*</span>}
            </label>

            {nested && typeOf(field).multiple ? (
              <SealedListEditor
                field={field}
                schema={nested}
                value={current}
                onChange={(next) => set(field.key, next)}
                disabled={disabled}
                idPrefix={id}
              />
            ) : nested ? (
              <div className="mt-1">
                <SealedEntryEditor
                  schema={nested}
                  value={isSealedValue(current) ? current : emptyEntry(nested, nested.variants[0]?.type ?? '')}
                  onChange={(next) => set(field.key, next)}
                  disabled={disabled}
                  idPrefix={id}
                />
              </div>
            ) : kind === 'boolean' ? (
              <input
                id={id}
                type="checkbox"
                className="mt-1 h-4 w-4"
                disabled={disabled}
                checked={current === true}
                onChange={(e) => set(field.key, e.target.checked)}
              />
            ) : kind === 'enum' ? (
              <select
                id={id}
                className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
                disabled={disabled}
                value={typeof current === 'string' ? current : ''}
                onChange={(e) => set(field.key, e.target.value || null)}
              >
                <option value="">—</option>
                {options(field).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : kind === 'int' || kind === 'decimal' ? (
              <input
                id={id}
                type="number"
                min={bound(field, 'min')}
                max={bound(field, 'max')}
                step={kind === 'int' ? 1 : 'any'}
                className="mt-1 w-32 rounded border border-gray-300 px-3 py-1.5 text-sm"
                disabled={disabled}
                value={current === null || current === undefined ? '' : String(current)}
                // Empty is "not authored", which is a different thing from zero — sending 0 for a
                // cleared box would author a value the member never typed.
                onChange={(e) => set(field.key, e.target.value === '' ? null : Number(e.target.value))}
              />
            ) : isTextList(field, current) ? (
              <input
                id={id}
                className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
                disabled={disabled}
                value={Array.isArray(current) ? current.join(', ') : ''}
                // A list is edited as the text between its commas, and always saved as a list —
                // including the first save, when the value it started from was absent.
                onChange={(e) =>
                  set(
                    field.key,
                    e.target.value
                      .split(',')
                      .map((entry) => entry.trim())
                      .filter(Boolean)
                  )
                }
              />
            ) : (
              <textarea
                id={id}
                rows={kind === 'text' && (bound(field, 'maxLength') ?? 0) > 120 ? 4 : 2}
                // The declared bound, applied. Without it the box accepts a bio the server then
                // refuses on save, which spends the writing before reporting the limit — and the
                // limit is already published here, so honoring it costs nothing.
                //
                // Deliberately NOT set on the list input above: there the bound is per entry, and
                // capping the joined comma-separated value would silently limit how many
                // certifications a coach may list.
                maxLength={bound(field, 'maxLength') ?? undefined}
                className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
                disabled={disabled}
                value={typeof current === 'string' ? current : ''}
                onChange={(e) => set(field.key, e.target.value === '' ? null : e.target.value)}
              />
            )}

            {field.description && <p className="mt-1 text-xs text-gray-500">{field.description}</p>}
          </div>
        )
      })}
    </div>
  )
}
